import { handValue, isAce, isTenValueCard } from './handMath.js';
import { MIN_BET, MAX_BET, BET_STEP } from './rules.js';

/**
 * @typedef {'conservative'|'casual'|'risky'} BjArchetype
 */

/**
 * @typedef {object} BjAiProfile
 * @property {BjArchetype} archetype
 * @property {number} tempoMsMin
 * @property {number} tempoMsMax
 */

/** @param {() => number} rng */
export function randomProfile(rng = Math.random) {
  const types = /** @type {const} */ (['conservative', 'casual', 'risky']);
  const archetype = types[Math.floor(rng() * types.length)];
  return profileForArchetype(archetype);
}

/** @param {BjArchetype} archetype */
export function profileForArchetype(archetype) {
  if (archetype === 'conservative') {
    return { archetype, tempoMsMin: 800, tempoMsMax: 1300 };
  }
  if (archetype === 'risky') {
    return { archetype, tempoMsMin: 500, tempoMsMax: 1100 };
  }
  return { archetype: 'casual', tempoMsMin: 700, tempoMsMax: 1500 };
}

/**
 * @param {number} bankroll
 * @param {BjAiProfile} profile
 * @param {() => number} rng
 */
export function pickBet(bankroll, profile, rng = Math.random) {
  if (bankroll < MIN_BET) return 0;
  let pctLo = 0.04;
  let pctHi = 0.08;
  if (profile.archetype === 'conservative') {
    pctLo = 0.02;
    pctHi = 0.05;
  } else if (profile.archetype === 'risky') {
    pctLo = 0.06;
    pctHi = 0.12;
  }
  const raw = bankroll * (pctLo + rng() * (pctHi - pctLo));
  let bet = Math.floor(raw / BET_STEP) * BET_STEP;
  bet = Math.max(MIN_BET, Math.min(bet, MAX_BET, bankroll));
  return bet;
}

/**
 * Dealer upcard value for strategy: 2–10 or 11 for Ace.
 * @param {import('../poker/cards.js').Card|null} up
 */
function dealerVal(up) {
  if (!up) return 7;
  if (isAce(up)) return 11;
  if (isTenValueCard(up)) return 10;
  return up.rank;
}

/**
 * @param {boolean} should
 * @param {BjAiProfile} profile
 * @param {() => number} rng
 */
function maybeMistake(should, profile, rng) {
  let err = 0.06;
  if (profile.archetype === 'conservative') err = 0.03;
  if (profile.archetype === 'risky') err = 0.1;
  if (rng() < err) return !should;
  return should;
}

/**
 * Basic strategy + archetype noise. Returns 'hit'|'stand'|'double'|'split'.
 * @param {object} ctx
 * @param {import('../poker/cards.js').Card[]} ctx.cards
 * @param {number} ctx.wager
 * @param {boolean} ctx.doubled
 * @param {boolean} ctx.fromSplitAces
 * @param {boolean} ctx.canDouble
 * @param {boolean} ctx.canSplit
 * @param {number} ctx.bankroll
 * @param {import('../poker/cards.js').Card|null} dealerUp
 * @param {BjAiProfile} profile
 * @param {() => number} rng
 */
export function pickAction(ctx, dealerUp, profile, rng = Math.random) {
  const { cards, fromSplitAces, canDouble, canSplit, bankroll } = ctx;
  if (fromSplitAces) {
    return 'stand';
  }
  const dv = dealerVal(dealerUp);
  const { total, soft } = handValue(cards);

  if (canSplit && cards.length === 2) {
    const split = basicSplit(cards, dv);
    if (split && maybeMistake(true, profile, rng)) return 'split';
    if (!split && !maybeMistake(false, profile, rng) && split) return 'split';
  }

  if (canDouble && bankroll >= ctx.wager && cards.length === 2) {
    const d = basicDouble(total, soft, cards, dv);
    if (d && maybeMistake(true, profile, rng)) return 'double';
  }

  const st = basicStand(total, soft, dv);
  if (maybeMistake(st, profile, rng)) return 'stand';
  return 'hit';
}

/**
 * @param {import('../poker/cards.js').Card[]} cards
 * @param {number} dv
 */
function basicSplit(cards, dv) {
  if (cards.length !== 2 || cards[0].rank !== cards[1].rank) return false;
  const r = cards[0].rank;
  if (r === 14) return true;
  if (r === 8) return true;
  if (r === 10 || r === 13 || r === 12 || r === 11) return false;
  if (r === 9) return [2, 3, 4, 5, 6, 8, 9].includes(dv);
  if (r === 7) return dv >= 2 && dv <= 7;
  if (r === 6) return dv >= 2 && dv <= 6;
  if (r === 5) return false;
  if (r === 4) return dv === 5 || dv === 6;
  if (r === 3 || r === 2) return dv >= 2 && dv <= 7;
  return false;
}

/**
 * @param {number} total
 * @param {boolean} soft
 * @param {import('../poker/cards.js').Card[]} cards
 * @param {number} dv
 */
function basicDouble(total, soft, cards, dv) {
  if (cards.length !== 2) return false;
  if (soft) {
    if (total === 13 || total === 14) return dv >= 5 && dv <= 6;
    if (total === 15 || total === 16) return dv >= 4 && dv <= 6;
    if (total === 17) return dv >= 2 && dv <= 6;
    if (total === 18) {
      if (dv >= 2 && dv <= 6) return true;
      if (dv === 7 || dv === 8) return false;
      return false;
    }
    if (total >= 19) return false;
    return false;
  }
  if (total === 9) return dv >= 3 && dv <= 6;
  if (total === 10) return dv >= 2 && dv <= 9;
  if (total === 11) return dv >= 2 && dv <= 10;
  return false;
}

/**
 * @param {number} total
 * @param {boolean} soft
 * @param {number} dv
 */
function basicStand(total, soft, dv) {
  if (total > 21) return true;
  if (soft) {
    if (total >= 19) return true;
    if (total === 18) return dv <= 8 && dv !== 1;
    return false;
  }
  if (total >= 17) return true;
  if (total === 16) return dv >= 2 && dv <= 6;
  if (total === 15) return dv >= 2 && dv <= 6;
  if (total === 14 || total === 13) return dv >= 2 && dv <= 6;
  if (total === 12) return dv >= 4 && dv <= 6;
  return false;
}
